import { getApp } from "firebase/app";
import {
    collection,
    getCountFromServer,
    getFirestore,
    query,
    where,
    type QueryConstraint,
    type WhereFilterOp,
} from "firebase/firestore";

export type CountQueryFilter = {
    field: string;
    op: WhereFilterOp;
    value: unknown | (() => unknown);
};

export type CountQuery = {
    path: string;
    field?: string;
    op?: WhereFilterOp;
    value?: unknown | (() => unknown);
    /** Tek sorguda birden fazla where (AND). */
    filters?: CountQueryFilter[];
};

export type HomeStatFilter = {
    field: string;
    op: WhereFilterOp;
    value: unknown | (() => unknown);
};

export type HomeStat = {
    key: string;
    label: string;
    /** Sidebar'da varsa tıklanınca o koleksiyona gider. */
    collectionPath?: string;
    /** Koleksiyon URL'sine FireCMS tablo filtresi ekler (birden fazla olabilir). */
    filter?: HomeStatFilter | HomeStatFilter[];
    queries: CountQuery[];
};

function encodeFilterValue(value: unknown): string {
    return value instanceof Date ? value.toISOString() : String(value);
}

export function buildCollectionSearch(
    filter?: HomeStatFilter | HomeStatFilter[],
): string {
    if (!filter) {
        return "";
    }
    const filters = Array.isArray(filter) ? filter : [filter];
    const params = new URLSearchParams();
    for (const item of filters) {
        const raw =
            typeof item.value === "function" ? item.value() : item.value;
        params.set(`${item.field}_op`, item.op);
        params.set(`${item.field}_value`, encodeFilterValue(raw));
    }
    return params.toString();
}

export function daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
}

function resolveCountFilters(spec: CountQuery): CountQueryFilter[] {
    if (spec.filters && spec.filters.length > 0) {
        return spec.filters;
    }
    if (spec.field && spec.op) {
        return [{ field: spec.field, op: spec.op, value: spec.value }];
    }
    return [];
}

export async function fetchCollectionCount(spec: CountQuery): Promise<number> {
    const db = getFirestore(getApp());
    const constraints: QueryConstraint[] = [];
    for (const item of resolveCountFilters(spec)) {
        const value =
            typeof item.value === "function" ? item.value() : item.value;
        constraints.push(where(item.field, item.op, value));
    }
    const snapshot = await getCountFromServer(
        constraints.length > 0
            ? query(collection(db, spec.path), ...constraints)
            : collection(db, spec.path),
    );
    return snapshot.data().count;
}

export async function fetchHomeStatValue(stat: HomeStat): Promise<number> {
    const counts = await Promise.all(stat.queries.map(fetchCollectionCount));
    return counts.reduce((sum, count) => sum + count, 0);
}
